import {ApplicationIcon} from '@orbit-ui/icons';
import {Link, RegisteredRouter} from '@tanstack/react-router';
import {
  ActivitySquareIcon,
  BookCopyIcon,
  BookDashedIcon,
  BookIcon,
  BookKeyIcon,
  ContainerIcon,
  DatabaseIcon,
  FileCogIcon,
  SettingsIcon,
  ShieldCheckIcon,
  ShieldEllipsisIcon,
  UsersIcon,
} from 'lucide-react';
import * as React from 'react';
import {useMemo} from 'react';
import {usePermissions} from '@/features/auth/hooks/use-permissions';
import {PERMISSIONS, Permission} from '@/features/auth/types/user';

type LinkProps = Pick<
  React.ComponentProps<typeof Link<RegisteredRouter>>,
  'to' | 'from' | 'params' | 'search'
>;

type PrimaryNavigationSection = {
  title: string;
  requiredPermission: Permission | null;
  items: Array<
    | ({
        title: string;
        icon?: React.ElementType;
        requiredPermission: Permission | null;
        subMenuItems?: Array<
          {
            title: string;
            icon?: React.ElementType;
            requiredPermission: Permission | null;
          } & LinkProps
        >;
      } & LinkProps)
    | {
        title: string;
        icon?: React.ElementType;
        requiredPermission: Permission | null;
        items: Array<
          {
            title: string;
            requiredPermission: Permission | null;
          } & LinkProps
        >;
      }
  >;
};

// Define the complete navigation structure with permission requirements
const NAVIGATION_CONFIG: PrimaryNavigationSection[] = [
  {
    title: 'Clusters',
    requiredPermission: null, // Always visible
    items: [
      {
        title: 'Overview',
        icon: ApplicationIcon,
        to: '/clusters/overview',
        requiredPermission: null, // Always visible
      },
      {
        title: 'Environments',
        icon: BookCopyIcon,
        requiredPermission: null,
        items: [
          {
            title: 'Overview',
            to: '/environments/overview',
            requiredPermission: null,
          },
          {
            title: 'Activity',
            to: '/environments/activity',
            requiredPermission: null,
          },
        ],
      },
    ],
  },
  {
    title: 'Tenants',
    requiredPermission: null,
    items: [
      {
        title: 'Overview',
        icon: UsersIcon,
        to: '/tenants/overview',
        requiredPermission: null,
      },
      {
        title: 'Activity',
        icon: ActivitySquareIcon,
        to: '/tenants/activity',
        requiredPermission: null,
      },
    ],
  },
  {
    title: 'Components',
    requiredPermission: PERMISSIONS.COMPONENT_VIEW,
    items: [
      {
        title: 'Registry',
        icon: BookIcon,
        to: '/components/registry',
        requiredPermission: null,
      },
      {
        title: 'Templates',
        icon: BookDashedIcon,
        requiredPermission: null,
        items: [
          {
            title: 'Recent',
            to: '/components/templates/recent',
            requiredPermission: null,
          },
          {
            title: 'Mine',
            to: '/components/templates/mine',
            requiredPermission: null,
          },
        ],
      },
      {
        title: 'Settings',
        icon: SettingsIcon,
        to: '/components/settings',
        requiredPermission: PERMISSIONS.CAPABILITY_MANAGE,
      },
    ],
  },
  {
    title: 'Applications',
    requiredPermission: null,
    items: [
      {
        title: 'Overview',
        icon: ApplicationIcon,
        to: '/applications/overview',
        requiredPermission: null,
      },
      {
        title: 'Deployments',
        icon: ContainerIcon,
        requiredPermission: null,
        items: [
          {
            title: 'Recent',
            to: '/applications/deployments/recent',
            requiredPermission: null,
          },
          {
            title: 'Mine',
            to: '/applications/deployments/mine',
            requiredPermission: null,
          },
        ],
      },
      {
        title: 'Configuration',
        icon: FileCogIcon,
        to: '/applications/configuration',
        requiredPermission: PERMISSIONS.CAPABILITY_MANAGE,
      },
    ],
  },
  {
    title: 'Administration',
    requiredPermission: PERMISSIONS.ADMIN_SSOCONFIG, // Entire section requires admin
    items: [
      {
        title: 'Authentication',
        icon: ShieldCheckIcon,
        to: '/admin/sso',
        requiredPermission: null, // Inherited from section
      },
      {
        title: 'Registries',
        icon: DatabaseIcon,
        to: '/admin/registries',
        requiredPermission: null, // Inherited from section
      },
      {
        title: 'Audit logs',
        icon: ShieldEllipsisIcon,
        to: '/admin/audit',
        requiredPermission: null, // Inherited from section
      },
      {
        title: 'API Keys',
        icon: BookKeyIcon,
        to: '/apikeys',
        requiredPermission: null, // Inherited from section
      },
    ],
  },
];

function useNavigation() {
  const permissions = usePermissions();

  // Pre-calculate permission-dependent values
  const isAdmin = !permissions.isLoading && permissions.isSsoAdmin();
  const canViewComponents = !permissions.isLoading && permissions.canViewComponents();
  const canManageCapabilities = !permissions.isLoading && permissions.canManageCapabilities();

  // Build navigation based on permissions
  return useMemo(() => {
    // If permissions are still loading, return empty navigation
    if (permissions.isLoading) {
      return [];
    }

    // Filter sections and items based on permissions
    return NAVIGATION_CONFIG.filter(
      (section) =>
        section.requiredPermission === null ||
        permissions.checkPermission(section.requiredPermission),
    )
      .map((section) => ({
        title: section.title,
        items: section.items.filter((item) => {
          // If the item has a specific permission requirement, check it
          if (item.requiredPermission) {
            return permissions.checkPermission(item.requiredPermission);
          }

          // Otherwise, it's visible
          return true;
        }),
      }))
      .filter((section) => section.items.length > 0); // Remove empty sections
  }, [
    permissions.isLoading,
    isAdmin,
    canViewComponents,
    canManageCapabilities,
    permissions.checkPermission,
  ]);
}

export {useNavigation};
